// CurriculumOutline — Sprint 19BH · v4 anchor-first ingest pillar B (frozen).
//
// Why this exists (v4-rewrite.md §2.2 + §0):
//   chapter → topic → concept three-tier frozen tree. Once user provides or LLM
//   extracts and user locks, the outline is the single reference truth for the
//   entire ingest pipeline. Every LLM-generated item must have its caseIds /
//   topicIds / conceptIds drawn from this outline — enforced by superRefine
//   (anchor-validation.ts) and audited post-hoc (audit/rules/tag-in-outline.ts).
//
// Why frozen:
//   19BG+3 F34 measured 14% chapter-id drift across N=3 runs (e.g.
//   `argentina_default` / `argentina_defaults` / `argentina_default_inflation`).
//   The drift is the direct symptom of anchor absence — per-stage LLM calls
//   each "invent" their own chapter-id namespace. v4 fixes this by making the
//   outline a first-class ctx citizen the LLM cannot redefine. I19 target:
//   chapter id jaccard ≥ 0.95 across N=3.
//
// Bottom-up composition (also export order in this file):
//   ConceptAnchor → TopicAnchor → ChapterAnchor → CurriculumOutline
//
// Constraints (v4-rewrite.md §2.2):
//   chapters: 1–20 — reasonable course-size envelope
//   topics:   1–5 per chapter — UI auto-collapses single-topic; cap matches
//                                  IB/cs3052/CFA-style course density
//   concepts: 3–12 per chapter — Day 6 baseline showed <3 starves cross-stage
//                                  cohesion; >12 fragments LLM attention
//   importance: 1|2|3, default 2 — mock-paper weighting hook (§12)
//
// outlineSchemaVersion — explicit evolution hook. v1 ships in 19BH. Future
//   bumps (add fields, change topic cap, etc.) land here so migration code
//   (idb.ts onupgradeneeded · workflows/finalize.ts) can dispatch by version.
//
// Thick (advanced) fields on ChapterAnchor are all optional:
//   targetItemCountsOverride — chapter-level quota override (e.g. chapter 7
//                              of IB is double-weight → 2× MCQ allotment)
//   focusedCognitiveLevels   — bias the chapter's generated items toward a
//                              subset of Bloom tiers. Spec §2.2 calls this
//                              `focusedBloomLevels`; we reuse the project's
//                              canonical `CognitiveLevelSchema` (5-tier Bloom,
//                              `cognitive-level.ts`) for naming consistency
//                              (CONTENT_SPEC v0.1.0).
//
// LLM_NATIVE_ARCHITECTURE compliance (llm-native.md §14):
//   §2.1 provenance — `source` enum makes the outline's origin explicit (user-
//                     typed vs LLM-generated-then-frozen). audit can surface
//                     gaps only meaningful for one source kind.
//   §2.3 visibility — `always` (outline is always shown in stage prompts).
import { z } from 'zod';
import { CognitiveLevelSchema } from './cognitive-level.js';
/**
 * Canonical id pattern shared across all outline anchors (chapter/topic/
 * concept). Snake-case, ASCII, must start with a lowercase letter, length
 * 1–64. Identical pattern prevents accidental id collisions across tiers when
 * an anchor is referenced bare.
 */
export const OUTLINE_ID_PATTERN = /^[a-z][a-z0-9_]{0,63}$/;
export const OUTLINE_ID_MESSAGE = 'must match /^[a-z][a-z0-9_]{0,63}$/ (lowercase ASCII, 1–64 chars, starting with letter)';
export const ConceptAnchorSchema = z.object({
    /** Stable id. Frozen for the entire course lifetime; cited by every item via `conceptIds[]`. */
    id: z.string().regex(OUTLINE_ID_PATTERN, OUTLINE_ID_MESSAGE)
        .describe('Stable id (snake_case ASCII). Cited verbatim from every item conceptIds[].'),
    /** Human-readable concept label. Shown in UI; LLM uses for prompt context. */
    label: z.string().min(1).max(120)
        .describe('Human-readable concept label (1–120 chars). Shown in UI; embedded in stage prompts.'),
    /** Alternative phrasings. Helps LLM match noisy source text to the concept. */
    aliases: z.array(z.string().min(1).max(120)).optional()
        .describe('Optional alternative phrasings for this concept. Used by LLM to resolve mentions in noisy source.'),
    /** Stages that MUST cover this concept. Default behavior comes from MANIFEST.outlineLevel. */
    mustCoverInStages: z.array(z.string().min(1).max(64)).optional()
        .describe('Optional list of stage ids that must cover this concept. Overrides MANIFEST defaults (thick mode).'),
}).passthrough();
export const TopicAnchorSchema = z.object({
    /** Stable id. Frozen for course lifetime; cited by items via `topicIds[]`. */
    id: z.string().regex(OUTLINE_ID_PATTERN, OUTLINE_ID_MESSAGE)
        .describe('Stable id (snake_case ASCII). Cited verbatim from every item topicIds[].'),
    /** Human-readable topic title. */
    title: z.string().min(1).max(120)
        .describe('Human-readable topic title (1–120 chars).'),
}).passthrough();
export const ChapterAnchorSchema = z.object({
    /** Stable id. Frozen for course lifetime; cited by items via `caseIds[]`. */
    id: z.string().regex(OUTLINE_ID_PATTERN, OUTLINE_ID_MESSAGE)
        .describe('Stable id (snake_case ASCII). Cited verbatim from every item caseIds[]. v4 I1 invariant.'),
    /** 1-based chapter order. Determines UI sidebar order + extract default. */
    order: z.number().int().min(1)
        .describe('1-based chapter order. Drives UI sidebar ordering and quota distribution.'),
    /** Human-readable chapter title. */
    title: z.string().min(1).max(120)
        .describe('Human-readable chapter title (1–120 chars).'),
    /** Optional secondary line (e.g. session number, date range). */
    subtitle: z.string().max(200).optional()
        .describe('Optional subtitle line shown in UI under the chapter title.'),
    /** Mock-paper weighting hook. 1 = light, 2 = normal (default), 3 = heavy. */
    importance: z.union([z.literal(1), z.literal(2), z.literal(3)]).default(2)
        .describe('Mock-paper weighting (1/2/3, default 2). Heavier chapters draw more mock items.'),
    /** Topics belonging to this chapter (1–5). UI auto-collapses single-topic. */
    topics: z.array(TopicAnchorSchema).min(1).max(5)
        .describe('1–5 topics. UI auto-collapses single-topic chapters. Items cite topicIds[].'),
    /** Concepts belonging to this chapter (3–12). Items cite conceptIds[]. */
    concepts: z.array(ConceptAnchorSchema).min(3).max(12)
        .describe('3–12 concepts. Lower bound prevents cross-stage cohesion starvation; upper bound prevents LLM attention fragmentation.'),
    /** Thick mode: override per-stage quota for this chapter. Default = MANIFEST.perChapterTarget. */
    targetItemCountsOverride: z.record(z.string(), z.number().int().nonnegative()).optional()
        .describe('Optional per-stage quota override for this chapter (thick mode). Keys are stage ids; values are target item counts.'),
    /** Thick mode: bias generation toward a subset of cognitive (Bloom) tiers. */
    focusedCognitiveLevels: z.array(CognitiveLevelSchema).optional()
        .describe('Optional Bloom-tier focus for this chapter (thick mode). Spec §2.2 names this focusedBloomLevels; we use the project canonical CognitiveLevel enum.'),
    /** DEBT-19BI-EXTRACT-CONFIRM-STEP: user free-text annotation per chapter.
     *  Injected into LLM prompt for this chapter's generation stages.
     *  User can note priorities, focus areas, or corrections here. */
    userHint: z.string().max(500).optional()
        .describe('Optional free-text annotation from user (max 500 chars). Injected into LLM prompt for this chapter\'s generation stages. Used during extract-confirm review step.'),
}).passthrough();
/** Origin of the outline. Audit uses this to suppress gaps meaningful only for one source kind. */
export const OutlineSourceSchema = z.enum(['user_provided', 'llm_generated_frozen']);
export const CurriculumOutlineSchema = z.object({
    /**
     * Schema version. Bump on breaking changes; idb.ts onupgradeneeded
     * dispatches migrations by version. Defaults to 'v1' (19BH ship).
     */
    outlineSchemaVersion: z.string().min(1).max(16).default('v1')
        .describe('Outline schema version. v1 ships in 19BH. Bump on breaking changes.'),
    /** Origin: user typed it vs LLM extracted and user locked. */
    source: OutlineSourceSchema
        .describe('Origin of the outline. user_provided = human typed it; llm_generated_frozen = LLM extracted and user locked.'),
    /** ISO timestamp when the outline was frozen. */
    generatedAt: z.string().datetime()
        .describe('ISO timestamp when this outline was frozen and committed to the course.'),
    /** Chapters in this course (1–20). */
    chapters: z.array(ChapterAnchorSchema).min(1).max(20)
        .describe('1–20 chapters. Frozen tree; LLM cannot expand or rename ids.'),
}).passthrough();
/** Default schema version literal used by code that constructs outlines programmatically. */
export const OUTLINE_SCHEMA_VERSION_V1 = 'v1';
//# sourceMappingURL=curriculum-outline.js.map